import { Link } from "wouter";
import { Clock, Flame, Users, UtensilsCrossed, Camera } from "lucide-react";
import { cn } from "@/lib/utils";
import type { RecipeRecord } from "@workspace/api-client-react";
import { useNutritionToggle } from "@/contexts/NutritionToggleContext";
import { NutritionLabel } from "@/components/NutritionLabel";
import type { NutritionSummary } from "@/components/NutritionLabel";
import { FavoriteButton } from "@/components/FavoriteButton";

interface RecipeCardProps {
  recipe: RecipeRecord;
  nutrition?: NutritionSummary | null;
  className?: string;
}

const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  "main dish":   { bg: "hsl(17 53% 51%)",  text: "hsl(40 33% 97%)" },
  "soup/stew":   { bg: "hsl(25 80% 45%)",  text: "hsl(40 33% 97%)" },
  bread:         { bg: "hsl(45 75% 55%)",  text: "hsl(33 28% 14%)" },
  dessert:       { bg: "hsl(350 55% 75%)", text: "hsl(33 28% 14%)" },
  beverage:      { bg: "hsl(210 50% 60%)", text: "hsl(40 33% 97%)" },
  salad:         { bg: "hsl(145 40% 45%)", text: "hsl(40 33% 97%)" },
  appetizer:     { bg: "hsl(38 85% 55%)",  text: "hsl(33 28% 14%)" },
  side:          { bg: "hsl(90 30% 50%)",  text: "hsl(40 33% 97%)" },
  breakfast:     { bg: "hsl(50 90% 60%)",  text: "hsl(33 28% 14%)" },
  snack:         { bg: "hsl(38 85% 55%)",  text: "hsl(33 28% 14%)" },
  sauce:         { bg: "hsl(91 18% 36%)",  text: "hsl(40 33% 97%)" },
  "sauce/condiment": { bg: "hsl(91 18% 36%)", text: "hsl(40 33% 97%)" },
};

const DEFAULT_COLOR = { bg: "hsl(30 15% 55%)", text: "hsl(40 33% 97%)" };

function getCategoryColor(category?: string | null) {
  if (!category) return DEFAULT_COLOR;
  const key = category.toLowerCase().trim();
  if (CATEGORY_COLORS[key]) return CATEGORY_COLORS[key];
  for (const [k, v] of Object.entries(CATEGORY_COLORS)) {
    if (key.includes(k) || k.includes(key)) return v;
  }
  return DEFAULT_COLOR;
}

function formatTime(time?: string | null): string | null {
  if (!time) return null;
  return time.replace(/\bminutes?\b/i, "min").replace(/\bhours?\b/i, "hr");
}

export function RecipeCard({ recipe, nutrition, className }: RecipeCardProps) {
  const { showNutrition } = useNutritionToggle();

  const heroUrl = recipe.heroImagePath
    ? `/api/recipes/hero-images/file/${recipe.id}-hero.jpg`
    : null;

  const prepTime = formatTime(recipe.prepTime);
  const cookTime = formatTime(recipe.cookTime);
  const servings = recipe.yieldServings;

  const { bg, text } = getCategoryColor(recipe.category);
  const titleLetter = (recipe.title.match(/[A-Za-z0-9]/)?.[0] ?? recipe.title.charAt(0)).toUpperCase();

  const showNutritionData = showNutrition && nutrition != null;

  return (
    <Link href={`/recipe/${recipe.id}`} className="group block">
      <article
        className={cn(
          "bg-white rounded-2xl overflow-hidden border border-border/50 shadow-sm",
          "transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 hover:border-primary/30",
          className
        )}
      >
        <div className="relative aspect-[4/3] overflow-hidden">
          {heroUrl ? (
            <img
              src={heroUrl}
              alt={recipe.title}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              onError={(e) => {
                const img = e.currentTarget;
                img.style.display = "none";
                const fallback = img.nextElementSibling as HTMLElement | null;
                if (fallback) fallback.style.display = "flex";
              }}
            />
          ) : null}

          <div
            className={cn(
              "w-full h-full flex items-center justify-center",
              heroUrl ? "hidden" : "flex"
            )}
            style={{ backgroundColor: bg }}
          >
            <span
              className="text-7xl font-bold select-none opacity-30"
              style={{
                fontFamily: "var(--font-display)",
                color: text,
                opacity: 0.35,
              }}
            >
              {titleLetter}
            </span>

            <div className="absolute bottom-3 right-3">
              <div className="bg-white/90 backdrop-blur-sm p-2 rounded-xl shadow-sm border border-white/40">
                <Camera className="w-4 h-4 text-muted-foreground" />
              </div>
            </div>
          </div>

          <div className="absolute top-2.5 right-2.5">
            <FavoriteButton
              recipeId={recipe.id}
              isFavorite={recipe.isFavorite === true}
            />
          </div>
        </div>

        <div className="p-4">
          <h3
            className="text-base font-semibold text-foreground leading-snug mb-1 line-clamp-2 group-hover:text-primary transition-colors"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {recipe.title}
          </h3>

          {(recipe.authorSource || recipe.cuisine) && (
            <p
              className="text-sm text-muted-foreground mb-2 line-clamp-1 italic"
              style={{ fontFamily: "var(--font-handwritten)", fontSize: "0.9rem" }}
            >
              {[recipe.authorSource, recipe.cuisine].filter(Boolean).join(" · ")}
            </p>
          )}

          {(prepTime || cookTime || servings) && (
            <div className="flex items-center gap-1.5 flex-wrap mb-3">
              {prepTime && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  {prepTime}
                </span>
              )}
              {cookTime && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">
                  <UtensilsCrossed className="w-3 h-3" />
                  {cookTime}
                </span>
              )}
              {servings && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">
                  <Users className="w-3 h-3" />
                  {servings}
                </span>
              )}
            </div>
          )}

          <div className="flex items-center justify-between gap-2">
            {recipe.category ? (
              <span
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                style={{ backgroundColor: bg, color: text }}
              >
                {recipe.category}
              </span>
            ) : (
              <span />
            )}

            {recipe.caloriesPerServing != null ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">
                <Flame className="w-3 h-3" />
                ~{recipe.caloriesPerServing} cal
              </span>
            ) : null}
          </div>

          {(() => {
            const recipeTags =
              (recipe as RecipeRecord & { tags?: { slug: string; name: string }[] })
                .tags ?? [];
            if (recipeTags.length === 0) return null;
            return (
              <div className="mt-2 flex flex-wrap gap-1">
                {recipeTags.slice(0, 3).map((t: { slug: string; name: string }) => (
                <span
                  key={t.slug}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-muted text-muted-foreground"
                >
                    {t.name}
                  </span>
                ))}
              </div>
            );
          })()}

          {showNutritionData && (
            <div className="mt-2 pt-2 border-t border-border/40">
              <p className="text-xs text-muted-foreground">
                {Math.round(nutrition.calories)} cal · {nutrition.protein_g.toFixed(1)}g protein · {nutrition.total_carbs_g.toFixed(1)}g carbs · {nutrition.total_fat_g.toFixed(1)}g fat
              </p>
              <NutritionLabel recipeId={recipe.id} />
            </div>
          )}
        </div>
      </article>
    </Link>
  );
}
